/**
 * StatusBadge — Chip/badge reutilizable para estados de contratos,
 * solicitudes, invitaciones, etc.
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils';

const statusBadgeVariants = cva(
    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold leading-5 whitespace-nowrap',
    {
        variants: {
            status: {
                draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
                posted: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                pending: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                accepted: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                rejected: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                completed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                cancelled: 'bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-300',
                incomplete: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
                in_transit: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
                delivered: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                info: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                error: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
            },
        },
        defaultVariants: {
            status: 'neutral',
        },
    }
);

const STATUS_LABELS: Record<string, string> = {
    draft: 'Borrador',
    posted: 'Publicada',
    active: 'Activo',
    pending: 'Pendiente',
    accepted: 'Aceptado',
    rejected: 'Rechazado',
    completed: 'Completado',
    cancelled: 'Cancelado',
    incomplete: 'Incompleto',
    in_transit: 'En tránsito',
    delivered: 'Entregado',
};

interface StatusBadgeProps extends VariantProps<typeof statusBadgeVariants> {
    /** Texto a mostrar. Si no se pasa, usa el label por defecto del status */
    label?: string;
    /** Mostrar indicador de punto (dot) */
    dot?: boolean;
    className?: string;
}

export function StatusBadge({ status, label, dot = false, className }: StatusBadgeProps) {
    const displayLabel = label ?? STATUS_LABELS[status ?? 'neutral'] ?? (status ?? 'neutral');

    return (
        <span className={cn(statusBadgeVariants({ status }), className)}>
            {dot && (
                <span className="size-1.5 shrink-0 rounded-full bg-current opacity-70" aria-hidden="true" />
            )}
            {displayLabel}
        </span>
    );
}
